import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { MEMBERSHIP_TOKEN_HEADER } from '../interceptors/membership-token.interceptor';
import {
  ApiEnvelope,
  DoctorGender,
  DoctorPage,
  PublicPriceList,
  PublicDoctor,
  CityValidateResult,
  CouponOrderKind,
  CouponValidateResult,
  PlanCatalog,
  PublicCoupon,
  MembershipCity,
  MembershipCycle,
  ContactInfo,
  MyReferral,
  ReferralProgram,
  PurchaseInitiateRequest,
  ReferralInput,
  ReferralResolveResult,
  ReferralValidateRequest,
  ReferralValidateResult,
  RenewalInfo,
  RenewalQuote,
  RenewalRequest,
  MembershipEntityType,
  HolderGender,
  MembershipSummary,
  OtpSendResult,
  OtpVerifyResult,
  PartnerPage,
  PatientRedemptionRow,
  PublicPartner,
  PublicPlan,
  PaymentVerifyResult,
  PurchaseInitiateResult,
  RawOtpVerifyResponse,
  RawRedemptionRow,
  RedemptionPage,
  UnavailableReason,
  PublicPage,
  PublicPageResult,
  PublicReviews,
  ReviewVisit,
  ReviewStars,
  ReviewTarget,
  LegalDocumentView,
  MyTermsStatus,
} from '../models/membership.model';
import { describePlanBenefit } from '../utils/plan-benefit-copy.util';
import { FindSortEnum, UnavailableReasonEnum } from '../models/membership.enums';

/** A refusal the screens can render honestly, instead of a raw HttpErrorResponse. */
export class MembershipApiError extends Error {
  constructor(
    readonly reason: UnavailableReason,
    message: string,
    readonly status: number,
    /**
     * The server's machine-readable refusal where it sends one (`CITY_NOT_AVAILABLE`,
     * `REFERRAL_INVALID`, ...). Optional and last, so every existing `new MembershipApiError(...)`
     * keeps compiling.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'MembershipApiError';
  }
}

/** The generic sentence a refusal carries when the server sent no message of its own (an HTML error page, an empty body). */
export const GENERIC_FAILURE_MESSAGE = 'That did not work. Please try again.';

/**
 * Set by `myMembership()` on the ONE answer that means "this verified number holds no membership": a 404 whose body carries the
 * server's own sentence. A 404 with no such sentence (a proxy's HTML page, an empty body, a missing route) stays a plain failure, so a
 * broken deployment is never shown to a member as "buy a plan" (review MR !14, S2).
 */
export const NO_MEMBERSHIP_CODE = 'NO_MEMBERSHIP';

const PUBLIC = 'api/membership/public';
const ME = 'api/membership/me';
/*
 * ⚠ THE PUBLISHED PAGE IS NOT UNDER `/public`. It lives at `/api/membership/marketing/*`, which
 * is public for the same structural reason — it sits outside every `/api/<role>/*` prefix, so no
 * role middleware runs. Moving it under a role prefix would make every partner's page answer 401.
 */
const MARKETING = 'api/membership/marketing';
/** How long the legal page waits for the Cancellation & Refund Policy before showing it as not published (review MR !9 N1). */
export const POLICY_READ_TIMEOUT_MS = 6000;

/**
 * Every Patient Web call, and the ONLY place a membership URL is written.
 *
 * Paths are contract 5.4 verbatim. Do not add one without the contract being
 * changed first - three other agents build against the same file.
 *
 * Flag-off is an HTTP 404 by contract 1.6 ("the gate runs ABOVE Joi; flag off
 * => 404 Not found"). That is why a 404 here becomes FEATURE_OFF rather than
 * "something went wrong": a member must be told the feature is not switched on,
 * not shown a broken page.
 */
@Injectable({ providedIn: 'root' })
export class MembershipApiService {
  private readonly http = inject(HttpClient);

  private readonly base = environment.BASE_URL.endsWith('/')
    ? environment.BASE_URL
    : `${environment.BASE_URL}/`;

  // ---- public -------------------------------------------------------------

  /**
   * ⚠ THE SERVER WRAPS THE LIST IN `plans`, NOT `rows` — verified against the
   * shipped controller (`successResponse(res, '', { plans: rows })`). Reading
   * `rows` first, as the contract implied, returned an EMPTY list against the real
   * backend while every unit test passed, because the test's own mock agreed with
   * the wrong name. `plans` is read first; the other two shapes are still accepted
   * so a rename degrades to one line rather than to a blank page.
   */
  listPlans(): Observable<PublicPlan[]> {
    return this.loadCatalog().pipe(map((catalog) => catalog.plans));
  }

  /** The plans, and whether the coupon field should be offered at all (owner, 2026-10-09). */
  loadCatalog(): Observable<PlanCatalog> {
    return this.get<PublicPlan[] | { plans?: PublicPlan[]; rows?: PublicPlan[]; couponsEnabled?: boolean }>(
      `${PUBLIC}/plans`,
    ).pipe(
      map((data) => ({
        plans: (Array.isArray(data) ? data : data?.plans ?? data?.rows ?? []).map(normalisePlan),
        couponsEnabled: Array.isArray(data) ? false : data?.couponsEnabled === true,
      })),
    );
  }

  /**
   * ⚠ TWO CORRECTIONS AGAINST THE SHIPPED BACKEND, both of which broke this screen.
   *
   *   1. The list comes back under `partners`, not `rows`.
   *   2. The only filters Joi accepts are `search`, `city`, `entityType`, `page`
   *      and `limit`. There is NO `specialty`, and `joiValidation` runs with Joi's
   *      default `allowUnknown: false` — so sending one is a 400 Validation Error,
   *      not a filter quietly ignored. `entityType` replaces it.
   */
  listPartners(query: {
    search?: string;
    city?: string;
    entityType?: MembershipEntityType;
    /** One partner's own row (its details page). */
    entityId?: string;
    specialty?: string;
    /** An everyday group (Heart, Kidney, Child ...): every speciality that belongs to it. */
    specialtyGroup?: string;
    /** `rating` asks the SERVER to order the whole directory; the default stays by name. */
    sort?: `${FindSortEnum.Name | FindSortEnum.Rating}`;
    page?: number;
    limit?: number;
  }): Observable<PartnerPage> {
    let params = new HttpParams();
    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.city) {
      params = params.set('city', query.city);
    }
    if (query.specialty) {
      params = params.set('specialty', query.specialty);
    }
    if (query.specialtyGroup) {
      params = params.set('specialtyGroup', query.specialtyGroup);
    }
    if (query.entityType) {
      params = params.set('entityType', query.entityType);
    }
    if (query.entityId) {
      params = params.set('entityId', query.entityId);
    }
    /*
     * ⚠ SENT ONLY WHEN IT IS THE NEW ORDER. Sending `sort=name` would be harmless today and is
     * still left out, so a request from this screen is byte-identical to what it was before the
     * control existed — which is what makes "nothing changed for anybody who does not use it"
     * checkable rather than asserted.
     */
    if (query.sort === FindSortEnum.Rating) {
      params = params.set('sort', FindSortEnum.Rating);
    }
    params = params.set('page', String(query.page ?? 1));
    // Bounded, always - CLAUDE.md governance rule 9. The screen pages; it never asks for everything.
    params = params.set('limit', String(query.limit ?? 20));

    return this.get<PartnerPage | PublicPartner[] | { partners?: PublicPartner[]; total?: number }>(
      `${PUBLIC}/partners`,
      params,
    ).pipe(
      map((data) => {
        if (Array.isArray(data)) {
          return { rows: data, total: data.length };
        }
        const rows =
          (data as { partners?: PublicPartner[] })?.partners ?? (data as PartnerPage)?.rows ?? [];
        return { rows, total: data?.total ?? rows.length };
      }),
    );
  }

  /** The fixed city list partners and patients choose from; every city carries its state. */
  listLocationCities(): Observable<Array<{ cityId: string; name: string; state: string }>> {
    return this.get<{ cities: Array<{ cityId: string; name: string; state: string }> }>(
      `${PUBLIC}/locations/cities`,
    ).pipe(map((data) => data?.cities ?? []));
  }

  /** The doctor specialities Lifemaan allows (the admin Speciality master). */
  listSpecialities(): Observable<string[]> {
    return this.get<{ specialities: Array<{ name: string }> }>(`${PUBLIC}/specialities`).pipe(
      map((data) => (data?.specialities ?? []).map((speciality) => speciality.name)),
    );
  }

  /** A partner's or a doctor's price list: the general prices, without discount. */
  getPrices(targetType: 'Hospital' | 'Laboratory' | 'Radio' | 'Doctor', targetId: string): Observable<PublicPriceList> {
    return this.get<PublicPriceList>(
      `${PUBLIC}/prices`,
      new HttpParams().set('targetType', targetType).set('targetId', targetId),
    );
  }

  /** The Doctors tab of Find care: doctors named on partners' published pages. */
  listDoctors(query: {
    search?: string;
    specialty?: string;
    specialtyGroup?: string;
    entityType?: MembershipEntityType;
    /** Only this partner's doctors (its details page). */
    partnerId?: string;
    /** Only doctors at partners in this city. */
    city?: string;
    sort?: `${FindSortEnum}`;
    gender?: DoctorGender;
    minExperience?: number;
    page?: number;
    limit?: number;
  }): Observable<DoctorPage> {
    let params = new HttpParams();
    if (query.city) {
      params = params.set('city', query.city);
    }
    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.specialty) {
      params = params.set('specialty', query.specialty);
    }
    if (query.specialtyGroup) {
      params = params.set('specialtyGroup', query.specialtyGroup);
    }
    if (query.entityType) {
      params = params.set('entityType', query.entityType);
    }
    if (query.partnerId) {
      params = params.set('partnerId', query.partnerId);
    }
    if (query.sort === FindSortEnum.Rating || query.sort === FindSortEnum.Experience) {
      params = params.set('sort', query.sort);
    }
    if (query.gender) {
      params = params.set('gender', query.gender);
    }
    if (query.minExperience) {
      params = params.set('minExperience', String(query.minExperience));
    }
    params = params.set('page', String(query.page ?? 1)).set('limit', String(query.limit ?? 20));
    return this.get<DoctorPage>(`${PUBLIC}/doctors`, params);
  }

  /** One doctor's own page. */
  getDoctor(doctorId: string): Observable<PublicDoctor> {
    return this.get<{ doctor: PublicDoctor }>(`${PUBLIC}/doctors/${encodeURIComponent(doctorId)}`).pipe(
      map((data) => data.doctor),
    );
  }

  /**
   * The one place a review target becomes a path, so no caller can build a second shape.
   *
   * ⚠ ADDRESSED BY THE PARTNER, NOT BY A PAGE SLUG (owner, 2026-09-24). It was the slug until then,
   * which tied every review to a PUBLISHED page — so a partner who had not built one could not be
   * rated at all. The listing card and the partner's page both pass the same pair through here,
   * which is what makes *"both opens the same review system"* true by construction rather than by
   * agreement between two screens.
   */
  private reviewPath(target: ReviewTarget): string {
    return `${encodeURIComponent(target.targetType)}/${encodeURIComponent(target.targetId)}`;
  }

  /**
   * The public rating of a partner (owner, 2026-09-21; retargeted 2026-09-24).
   *
   * ⚠ A PARTNER NOBODY HAS RATED ANSWERS, rather than failing. "No reviews yet" is a real answer,
   * and refusing would make a brand-new partner look like one that does not exist.
   */
  getPartnerReviews(target: ReviewTarget): Observable<PublicReviews> {
    return this.get<PublicReviews>(`${PUBLIC}/partners/${this.reviewPath(target)}/reviews`);
  }

  /** This member's visits to the partner, newest first, each with its own review if one was left. */
  getMyVisits(target: ReviewTarget): Observable<ReviewVisit[]> {
    return this.get<{ visits: ReviewVisit[] }>(`${ME}/reviews/${this.reviewPath(target)}`).pipe(
      map((data) => data?.visits ?? []),
    );
  }

  /** Review one visit, or change that visit's review (owner, 2026-10-21: one review per partner per visit). */
  submitReview(
    target: ReviewTarget,
    redemptionId: string,
    stars: ReviewStars,
  ): Observable<{ overall: number; updated: boolean }> {
    return this.post<{ overall: number; updated: boolean }>(`${ME}/reviews/${this.reviewPath(target)}`, {
      ...stars,
      redemptionId,
    });
  }

  /** Take back the review of one visit. */
  withdrawReview(target: ReviewTarget, redemptionId: string): Observable<{ withdrawn: boolean }> {
    return this.delete<{ withdrawn: boolean }>(
      `${ME}/reviews/${this.reviewPath(target)}`,
      new HttpParams().set('redemptionId', redemptionId),
    );
  }

  sendOtp(countryPhoneCode: string, phone: string): Observable<OtpSendResult> {
    // The server deliberately never says whether a membership exists (contract 5.1),
    // so this response is the same for a member and a stranger.
    return this.post<OtpSendResult>(`${PUBLIC}/otp/send`, { countryPhoneCode, phone });
  }

  /**
   * ⚠ THE SERVER CALLS THE TOKEN `token`, NOT `sessionToken`. Normalised here so
   * every screen keeps one name; both are read, so whichever way the two are
   * reconciled later this keeps working.
   */
  verifyOtp(countryPhoneCode: string, phone: string, otp: string): Observable<OtpVerifyResult> {
    return this.post<RawOtpVerifyResponse>(`${PUBLIC}/otp/verify`, {
      countryPhoneCode,
      phone,
      otp,
    }).pipe(
      map((raw) => ({
        sessionToken: raw?.token ?? raw?.sessionToken ?? '',
        expiresAt: raw?.expiresAt,
        hasMembership: raw?.hasMembership ?? Boolean(raw?.membership),
        membership: raw?.membership ?? null,
      })),
    );
  }

  /**
   * ⚠ THE TOKEN GOES IN THE HEADER HERE, NOT THE BODY, AND THE CONTRACT SAID
   * OTHERWISE. The shipped handler calls `resolveMemberToken(req)`, which reads
   * `req.headers['x-membership-token']`, and validates the body against
   * `{ planVersionId, holderName }` ONLY. So the contract's
   * `{ planVersionId, holderName, token }` failed twice over: `token` is an
   * unknown key, which Joi refuses with a 400, and the header the handler wanted
   * was never sent, which is a 401. The header is set explicitly rather than by
   * widening the interceptor, so the interceptor keeps its narrow /me/ rule and
   * this one deliberate exception is visible at the call site.
   */
  initiatePurchase(
    planVersionId: string,
    holderName: string,
    token: string,
  ): Observable<PurchaseInitiateResult> {
    return this.post<PurchaseInitiateResult>(`${PUBLIC}/purchase/initiate`, { planVersionId, holderName }, memberHeaders(token));
  }

  /**
   * Initiate with city, referral and credit (Builder-Growth contract 4.2).
   *
   * A SEPARATE METHOD, NOT A WIDER `initiatePurchase`. The old one sends exactly
   * `{ planVersionId, holderName }`; this one sends the growth body. Keeping both means a screen can
   * never half-send the new shape, and the old method's specs stay true.
   */
  initiateGrowthPurchase(request: PurchaseInitiateRequest, token: string): Observable<PurchaseInitiateResult> {
    const body: PurchaseInitiateRequest = {
      planVersionId: request.planVersionId,
      holderName: request.holderName,
      cityId: request.cityId,
      source: request.source,
    };
    if (request.agreedToTerms === true) {
      body.agreedToTerms = true;
    }
    const referral = compactReferral(request.referral);
    if (referral) {
      body.referral = referral;
    }
    if (request.applyReferralCredit !== undefined) {
      body.applyReferralCredit = request.applyReferralCredit;
    }
    // Optional holder details (2026-09-17). Sent only when something was filled in; the server refuses unknown keys.
    if (request.holderProfile && Object.keys(request.holderProfile).length) {
      body.holderProfile = request.holderProfile;
    }
    // A coupon the server already accepted at validate time; it is checked again when the order is made.
    if (request.couponCode) {
      body.couponCode = request.couponCode;
    }
    return this.post<PurchaseInitiateResult>(`${PUBLIC}/purchase/initiate`, body, memberHeaders(token));
  }

  // ---- terms & conditions (owner, 2026-10-06) -------------------------------

  /**
   * The patient wording, for the tick on the purchase page. UNAUTHENTICATED and answers 404 while the
   * terms are switched off — read here as "nothing to agree to".
   *
   * ⚠ ONLY A 404 MEANS "NOTHING TO AGREE TO" (review MR !1 R4). Any other failure is passed on: read as an
   * empty list it would let a member pay without being shown terms that do exist.
   */
  legalDocuments(): Observable<LegalDocumentView[]> {
    return this.get<{ documents: LegalDocumentView[] }>('api/legal/documents', new HttpParams().set('audience', 'PATIENT')).pipe(
      map((data) => data?.documents || []),
      catchError((error: MembershipApiError) => (error.status === 404 ? of([] as LegalDocumentView[]) : throwError(() => error))),
    );
  }

  /**
   * The Cancellation & Refund Policy, to be READ (it is not one of the documents a person agrees to, so it is not in
   * legalDocuments()). null when nothing is published or the feature is off - the page then says so, and never blocks.
   */
  cancellationRefundPolicy(): Observable<LegalDocumentView | null> {
    const params = new HttpParams().set('type', 'CANCELLATION_REFUND').set('audience', 'PATIENT');
    return this.get<LegalDocumentView>('api/legal/document', params).pipe(
      // A slow policy must not hold the Terms and Privacy behind their skeleton (review MR !9 N1): give up and show it as not published.
      timeout(POLICY_READ_TIMEOUT_MS),
      map((document) => (document && document.source === 'PUBLISHED' && document.bodyHtml ? document : null)),
      catchError(() => of(null)),
    );
  }

  /** Whether this member has a newer version to read and agree to. Never blocks anything. */
  getMyTerms(): Observable<MyTermsStatus> {
    return this.get<MyTermsStatus>(`${ME}/terms`);
  }

  /** One explicit agreement, recorded against this mobile number. */
  acceptMyTerms(): Observable<MyTermsStatus> {
    return this.post<MyTermsStatus>(`${ME}/terms/accept`, { agreed: true });
  }

  // ---- growth: cities -----------------------------------------------------

  /**
   * Cities where membership can be bought. FILTERED ON THE SERVER: the search term is sent and the
   * answer is rendered as it comes. No city is hard-coded or filtered out here (ADD 6).
   */
  listCities(search = ''): Observable<MembershipCity[]> {
    let params = new HttpParams();
    const term = search.trim();
    if (term) {
      params = params.set('search', term);
    }
    return this.get<MembershipCity[] | { cities?: MembershipCity[]; rows?: MembershipCity[] }>(
      `${PUBLIC}/cities`,
      params,
    ).pipe(
      map((data) => (Array.isArray(data) ? data : data?.cities ?? data?.rows ?? [])),
      map((rows) => sortCitiesByName(rows.map(normaliseCity))),
    );
  }

  validateCity(cityId: string): Observable<CityValidateResult> {
    return this.post<CityValidateResult>(`${PUBLIC}/cities/validate`, { cityId });
  }

  // ---- growth: referral ---------------------------------------------------

  resolveReferral(ref: string): Observable<ReferralResolveResult> {
    const params = new HttpParams().set('ref', ref);
    return this.get<ReferralResolveResult>(`${PUBLIC}/referral/resolve`, params);
  }

  /** Needs the member token: the server must know who is asking, to refuse a self-referral. */
  validateReferral(input: ReferralValidateRequest, token: string): Observable<ReferralValidateResult> {
    const body: ReferralValidateRequest = { ...(compactReferral(input) ?? {}) };
    if (input.planVersionId) {
      body.planVersionId = input.planVersionId;
    }
    return this.post<ReferralValidateResult>(`${PUBLIC}/referral/validate`, body, memberHeaders(token));
  }

  // ---- coupons (owner, 2026-10-09) ------------------------------------------

  /**
   * Does this code work for this member on this plan? Read-only, needs the member token (it must know whose
   * number is asking). A code that does not work is `valid: false` with a sentence - NOT an HTTP error.
   */
  validateCoupon(
    input: { code: string; planVersionId: string; kind: CouponOrderKind },
    token: string,
  ): Observable<CouponValidateResult> {
    return this.post<CouponValidateResult>(`${PUBLIC}/coupon/validate`, input, memberHeaders(token));
  }

  /**
   * The codes an admin switched on for everybody (owner, 2026-10-28). Needs no sign-in; with the member token the server leaves out the
   * codes that member has already used. NEVER an error: a failure, or coupons being off, is simply "no offers".
   */
  publicCoupons(input: { planVersionId: string | null; kind: CouponOrderKind }, token: string | null): Observable<PublicCoupon[]> {
    let params = new HttpParams().set('kind', input.kind);
    if (input.planVersionId) {
      params = params.set('planVersionId', input.planVersionId);
    }
    return this.request(
      this.http.get<ApiEnvelope<{ coupons?: PublicCoupon[] }>>(`${this.base}${PUBLIC}/coupons`, {
        params,
        ...(token ? { headers: memberHeaders(token) } : {}),
      }),
    ).pipe(
      map((data) => (Array.isArray(data?.coupons) ? data.coupons : [])),
      catchError(() => of([] as PublicCoupon[])),
    );
  }

  // ---- me: referral, cycles, renewal -------------------------------------

  /** What a referral earns, for visitors signed in or not. null when it cannot be read: the page then simply shows no benefit card. */
  referralProgram(): Observable<ReferralProgram | null> {
    return this.get<ReferralProgram>(`${PUBLIC}/referral/program`).pipe(
      map((program) => (program && program.enabled ? program : null)),
      catchError(() => of(null)),
    );
  }

  /**
   * Lifemaan's email and phone for the "Contact us" section. null when none is set or it cannot be read: the section is then simply not
   * drawn, and nothing else on the page is affected.
   */
  contactInfo(): Observable<ContactInfo | null> {
    return this.get<ContactInfo>(`${PUBLIC}/contact`).pipe(
      map((info) => (info && (info.email || info.phone) ? info : null)),
      catchError(() => of(null)),
    );
  }

  myReferral(): Observable<MyReferral> {
    return this.get<MyReferral>(`${ME}/referral`).pipe(map(normaliseReferral));
  }

  myCycles(): Observable<MembershipCycle[]> {
    return this.get<MembershipCycle[] | { cycles?: MembershipCycle[]; rows?: MembershipCycle[] }>(
      `${ME}/cycles`,
    ).pipe(map((data) => (Array.isArray(data) ? data : data?.cycles ?? data?.rows ?? [])));
  }

  myRenewal(): Observable<RenewalInfo> {
    return this.get<RenewalInfo>(`${ME}/renewal`).pipe(
      map((info) => ({
        ...info,
        plans: info?.plans ?? [],
        availableCredit: info?.availableCredit ?? 0,
      })),
    );
  }

  quoteRenewal(request: RenewalRequest): Observable<RenewalQuote> {
    return this.post<RenewalQuote>(`${ME}/renewal/quote`, {
      planVersionId: request.planVersionId,
      cityId: request.cityId,
      applyReferralCredit: request.applyReferralCredit,
      ...(request.couponCode ? { couponCode: request.couponCode } : {}),
    });
  }

  initiateRenewal(request: RenewalRequest): Observable<PurchaseInitiateResult> {
    return this.post<PurchaseInitiateResult>(`${ME}/renewal/initiate`, {
      planVersionId: request.planVersionId,
      cityId: request.cityId,
      applyReferralCredit: request.applyReferralCredit,
      source: request.source ?? 'PATIENT_WEB',
      ...(request.couponCode ? { couponCode: request.couponCode } : {}),
    });
  }

  // ---- me (needs the x-membership-token header) ---------------------------

  myMembership(): Observable<MembershipSummary> {
    return this.get<MembershipSummary>(`${ME}/membership`).pipe(
      map(normaliseSummary),
      catchError((error: unknown) =>
        error instanceof MembershipApiError &&
        error.status === 404 &&
        error.reason === UnavailableReasonEnum.Server &&
        !error.code &&
        error.message !== GENERIC_FAILURE_MESSAGE
          ? throwError(() => new MembershipApiError(error.reason, error.message, error.status, NO_MEMBERSHIP_CODE))
          : throwError(() => error),
      ),
    );
  }

  /** The everyday groups the Find care icon row draws (Heart, Kidney, Child ...). Served, never copied into the site. */
  specialtyGroups(): Observable<Array<{ key: string; label: string }>> {
    return this.get<{ groups?: Array<{ key: string; label: string }> }>(`${PUBLIC}/specialty-groups`).pipe(map((data) => data?.groups ?? []));
  }

  /** The holder's own details on their LifeCard (the profile page). Only what is sent is saved. */
  updateMyProfile(body: { holderName?: string; holderProfile?: { gender: HolderGender | null; age: number | null; address: string } }): Observable<MembershipSummary> {
    return this.put<MembershipSummary>(`${ME}/profile`, body).pipe(map(normaliseSummary));
  }

  /**
   * D-B4: the member has now SEEN the "new referral credit" notice. Idempotent on the server, but the
   * screen still sends it once per notice — see `MyMembershipComponent.markCreditSeen`.
   */
  markReferralCreditSeen(): Observable<{ marked: number }> {
    return this.post<{ marked: number }>(`${ME}/referral/credit-seen`, {});
  }

  /**
   * ⚠ The envelope is right (`rows` + `total`) but almost every FIELD NAME inside a
   * row differs from the contract's — see `RawRedemptionRow`. Normalised so the
   * screens keep the contract's vocabulary.
   */
  myRedemptions(page = 1, limit = 20): Observable<RedemptionPage> {
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    return this.get<{ rows?: RawRedemptionRow[]; total?: number }>(
      `${ME}/redemptions`,
      params,
    ).pipe(
      map((data) => ({
        rows: (data?.rows ?? []).map(normaliseRedemptionRow),
        total: data?.total ?? 0,
      })),
    );
  }

  /**
   * One partner's published page.
   *
   * ⚠ A DRAFT, A PAGE TAKEN DOWN AND A PAGE THAT NEVER EXISTED ALL ANSWER 404, on purpose —
   * telling a caller that a page exists but is hidden would let anybody enumerate which clinics are
   * preparing one. The screen therefore says "not found" for all three, which is the truth it is
   * entitled to.
   */
  /*
   * ⚠ GENERIC OVER THE PAGE SHAPE (review MR !1 S37). The page screen reads the builder pack's richer shape
   * (`BuilderPublicPage`, declared beside that screen); asking for it here types the answer at the HTTP boundary - the one
   * place an answer is typed at all - instead of a double cast inside the screen.
   */
  getPublicPage<P = PublicPage>(slug: string): Observable<PublicPageResult<P>> {
    return this.get<PublicPageResult<P>>(`${MARKETING}/page/${encodeURIComponent(slug)}`);
  }

  /**
   * Where uploaded pictures live.
   *
   * ⚠ A STORED PICTURE IS A KEY, NOT A URL — and some of them really are URLs, because the page
   * editor accepted pasted addresses before it could upload. Whoever draws one has to tell the two
   * apart; this is only the prefix for the first kind.
   */
  get imageBase(): string {
    return environment.IMAGE_BASE_URL.endsWith('/')
      ? environment.IMAGE_BASE_URL
      : `${environment.IMAGE_BASE_URL}/`;
  }

  // ---- plumbing -----------------------------------------------------------

  /*
   * ⚠ THE ONLY THREE WAYS A REQUEST LEAVES THIS SERVICE (review MR !1 S14). Each one sends through `request`, which reads the
   * product's envelope (`unwrap`) and turns every failure into a `MembershipApiError` - so no method can return the envelope by
   * mistake or let a raw HttpErrorResponse reach a screen. A call that needs the member token passes `memberHeaders(token)`.
   */
  private get<T>(path: string, params?: HttpParams): Observable<T> {
    return this.request(this.http.get<ApiEnvelope<T>>(`${this.base}${path}`, { params }));
  }

  /**
   * The return screen after Cashfree (C1). `ref` is the random gateway id from the return URL — an
   * order number alone finds nothing. The token is sent when this tab still has one, so the server can
   * include the new membership's summary; without it the status is still answered.
   */
  verifyPayment(orderNo: string, ref: string, token: string | null): Observable<PaymentVerifyResult> {
    return this.post<PaymentVerifyResult>(`${PUBLIC}/payment/verify`, { orderNo, ref }, token ? memberHeaders(token) : undefined);
  }

  private post<T>(path: string, body: unknown, headers?: Record<string, string>): Observable<T> {
    return this.request(this.http.post<ApiEnvelope<T>>(`${this.base}${path}`, body, headers ? { headers } : {}));
  }

  private put<T>(path: string, body: unknown): Observable<T> {
    return this.request(this.http.put<ApiEnvelope<T>>(`${this.base}${path}`, body));
  }

  private delete<T>(path: string, params?: HttpParams): Observable<T> {
    return this.request(this.http.delete<ApiEnvelope<T>>(`${this.base}${path}`, { params }));
  }

  private request<T>(call: Observable<ApiEnvelope<T>>): Observable<T> {
    return call.pipe(map(unwrap), catchError(toMembershipError));
  }
}

/**
 * ⚠ THE TOKEN GOES IN THE HEADER FOR THE FEW /public CALLS THAT NEED IT. The interceptor adds it to /me/ only; these are the
 * deliberate exceptions, and naming them at the call site keeps the interceptor's rule narrow.
 */
function memberHeaders(token: string): Record<string, string> {
  return { [MEMBERSHIP_TOKEN_HEADER]: token };
}

/**
 * The envelope can answer success:false with HTTP 200 - several apiResponse
 * helpers in this product return 200 with the real status commented out
 * (contract 5, a documented trap). So a 200 is not evidence of success and the
 * body has to be read.
 */
export function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (!envelope || envelope.success === false) {
    throw new MembershipApiError(
      UnavailableReasonEnum.Server,
      envelope?.message || GENERIC_FAILURE_MESSAGE,
      200,
    );
  }
  return envelope.data;
}

export function toMembershipError(error: unknown): Observable<never> {
  if (error instanceof MembershipApiError) {
    return throwError(() => error);
  }
  if (error instanceof HttpErrorResponse) {
    // status 0 is a dead connection, a blocked request or CORS - never a server answer.
    if (error.status === 0) {
      return throwError(
        () =>
          new MembershipApiError(
            UnavailableReasonEnum.Network,
            'We could not reach Lifemaan. Check your connection and try again.',
            0,
          ),
      );
    }
    if (error.status === 404 && isFeatureGateRefusal(error.error)) {
      return throwError(
        () => new MembershipApiError(UnavailableReasonEnum.FeatureOff, 'Lifemaan Membership is not switched on yet.', 404),
      );
    }
    const rawMessage = (error.error as { message?: unknown } | null)?.message;
    const message = typeof rawMessage === 'string' ? rawMessage : '';
    return throwError(
      () =>
        new MembershipApiError(
          UnavailableReasonEnum.Server,
          message || GENERIC_FAILURE_MESSAGE,
          error.status,
          errorCodeOf(error.error),
        ),
    );
  }
  return throwError(() => new MembershipApiError(UnavailableReasonEnum.Server, GENERIC_FAILURE_MESSAGE, -1));
}

/**
 * Turn a charge-category code into something a patient can read: `OT_CHARGES`
 * becomes "OT charges". Only ever applied to a CATEGORY code — never to a line
 * description, which is where clinical detail lives (D-018).
 */
export function humaniseServiceCode(code: string): string {
  const words = String(code).replace(/_/g, ' ').trim().toLowerCase();
  if (!words) {
    return '';
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The server's row -> the shape the screens read.
 *
 * `beneficiaryName` deliberately has NO fallback to the holder's name. D-018 makes
 * the beneficiary the whole accountability mechanism: a redemption is visible as
 * wrong precisely because the holder does not recognise the name on it. Quietly
 * substituting their own name would hide exactly the case the field exists to
 * expose, so an absent name stays absent and the screen shows the row without it.
 */
export function normaliseRedemptionRow(raw: RawRedemptionRow): PatientRedemptionRow {
  const serviceType =
    raw.serviceType ||
    (raw.serviceTypes?.length
      ? raw.serviceTypes.map(humaniseServiceCode).filter(Boolean).join(' · ')
      : undefined);

  return {
    redemptionNo: raw.redemptionNo,
    beneficiaryName: raw.beneficiaryName ?? '',
    entityType: (raw.partnerType ?? raw.entityType ?? 'Hospital') as MembershipEntityType,
    entityName: raw.partnerName ?? raw.entityName ?? '',
    // The server sends one `date` (the reservation instant). Kept in `reservedAt`
    // so `rowDate`'s confirmed-then-reserved preference still reads correctly.
    confirmedAt: raw.confirmedAt ?? undefined,
    reservedAt: raw.date ?? raw.reservedAt,
    invoiceTotal: raw.invoiceTotal ?? 0,
    eligibleTotal: raw.eligibleTotal ?? 0,
    benefitTotal: raw.benefitTotal ?? 0,
    firstVisitWaiverApplied: raw.firstVisitWaiverApplied,
    status: raw.status,
    serviceType,
    remainingAfter: raw.remainingAfter,
  };
}

/**
 * INTERPRETATION, raised to the lead session: contract 5.4 says /public/plans
 * returns "published versions only, with maxBenefit computed" but does not pin the
 * projection's field names. Section 2 names them code / name on MembershipPlan, so
 * those are what this app types - and the two obvious alternatives are accepted
 * here so a naming difference degrades to one line changing rather than to every
 * plan card rendering blank.
 *
 * maxBenefit is deliberately NOT computed client-side when it is missing. The
 * server owns that number (price * benefitMultiplier); computing a second copy
 * here would let the page promise a saving the entitlement does not carry.
 *
 * ⚠ THE DESCRIPTION'S PERCENTAGE IS DERIVED HERE, AND THIS IS THE ONLY PLACE PLANS ENTER THE APP —
 * `listPlans()` is the sole source for both the plans page and the purchase page, so a figure
 * cannot reach one screen stale and the other correct. The stored description is authored free text
 * that quotes a percentage; the entitlement lives in `benefitPercent`, and on 2026-09-24 they had
 * diverged on the live catalogue. See `describePlanBenefit` for the measurement.
 */
export function normalisePlan(raw: PublicPlan & { planName?: string; planCode?: string }): PublicPlan {
  return {
    ...raw,
    name: raw.name ?? raw.planName ?? '',
    code: raw.code ?? raw.planCode ?? '',
    description: describePlanBenefit(raw.description, raw.benefitPercent, raw.entityBenefitPercent),
  };
}

/** The code the feature gate sends with its 404 once the backend tags it (review MR !1 S2). */
export const FEATURE_OFF_CODE: string = UnavailableReasonEnum.FeatureOff;

/**
 * Is this 404 the feature gate saying "not switched on" (review MR !1 S2)?
 *
 *   1. The gate's error CODE, when the server sends one (`FEATURE_OFF`) - the only reliable signal, and the one to keep.
 *   2. Until then, the gate's own envelope: a JSON `{ success: false }` whose message is absent or exactly "Not found" /
 *      "Route not found". Any other 404 carries a real sentence ("No membership was found for this mobile number.") and calling
 *      that "Membership is not open yet" told members with a perfectly good card that the whole product was closed
 *      (owner, 2026-10-19).
 *
 * ⚠ AN HTML ERROR PAGE, A BARE STRING OR AN EMPTY BODY IS NOT THE GATE. A proxy or a missing route in front of the API answers like
 * that, and reading it as "switched off" hid a broken deployment behind a calm message.
 */
export function isFeatureGateRefusal(body: unknown): boolean {
  if (errorCodeOf(body) === FEATURE_OFF_CODE) {
    return true;
  }
  if (!body || typeof body !== 'object' || (body as { success?: unknown }).success !== false) {
    return false;
  }
  const message = (body as { message?: unknown }).message;
  if (message === undefined || message === null) {
    return true;
  }
  return typeof message === 'string' && /^(not found|route not found)$/i.test(message.trim());
}

/**
 * The refusal code, wherever the server put it. `CustomError(409, message, { code })` reaches the
 * browser as `data.code` in this product's envelope; a bare `code` and `errors.code` are accepted so
 * a different helper does not turn a readable refusal into a generic one.
 */
export function errorCodeOf(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }
  const envelope = body as { code?: unknown; data?: { code?: unknown }; errors?: { code?: unknown } };
  const code = envelope.data?.code ?? envelope.code ?? envelope.errors?.code;
  return typeof code === 'string' && code ? code : undefined;
}

/** Only the half of a referral that was actually given. A token wins over a typed number. */
export function compactReferral(input: ReferralInput | null | undefined): ReferralInput | undefined {
  if (!input) {
    return undefined;
  }
  const ref = input.ref?.trim();
  if (ref) {
    return { ref };
  }
  const phone = input.referrerPhone?.replace(/\D/g, '');
  if (phone) {
    const out: ReferralInput = { referrerPhone: phone };
    if (input.referrerCountryPhoneCode) {
      out.referrerCountryPhoneCode = input.referrerCountryPhoneCode;
    }
    return out;
  }
  return undefined;
}

/**
 * Keeps `referralCredit` numeric and bounded. A missing block stays missing (the tile does not draw);
 * a present one never carries NaN or a negative into a rupee figure.
 */
export function normaliseSummary(raw: MembershipSummary): MembershipSummary {
  const credit = raw?.referralCredit;
  if (!credit) {
    return raw;
  }
  const amount = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };
  return {
    ...raw,
    referralCredit: {
      available: amount(credit.available),
      earnedTotal: amount(credit.earnedTotal),
      addedToBenefit: amount(credit.addedToBenefit),
      unseenAmount: amount(credit.unseenAmount),
      unseenCount: amount(credit.unseenCount),
      lastEarnedAt: credit.lastEarnedAt ?? null,
      usableFor: credit.usableFor ?? 'NONE',
    },
  };
}

/** Alphabetical by name, case-insensitive (D-B3). The server already sorts; this is the defensive copy. */
export function sortCitiesByName(cities: MembershipCity[]): MembershipCity[] {
  return [...cities].sort(
    (a, b) =>
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) ||
      (a.state ?? '').localeCompare(b.state ?? '', 'en', { sensitivity: 'base' }),
  );
}

export function normaliseCity(
  raw: MembershipCity & { _id?: string; cityName?: string; stateName?: string },
): MembershipCity {
  return {
    cityId: raw.cityId ?? raw._id ?? '',
    name: raw.name ?? raw.cityName ?? '',
    state: raw.state ?? raw.stateName ?? '',
  };
}

export function normaliseReferral(raw: MyReferral): MyReferral {
  const summary: Partial<MyReferral['summary']> = raw?.summary ?? {};
  return {
    ...raw,
    enabled: !!raw?.enabled,
    shareUrl: raw?.shareUrl ?? raw?.webUrl ?? '',
    shareMessage: raw?.shareMessage ?? '',
    summary: {
      total: summary.total ?? 0,
      successful: summary.successful ?? 0,
      pending: summary.pending ?? 0,
      earned: summary.earned ?? 0,
      used: summary.used ?? 0,
      available: summary.available ?? 0,
      addedToBenefit: summary.addedToBenefit ?? 0,
      nextExpiry: summary.nextExpiry ?? null,
    },
    history: raw?.history ?? [],
  };
}
